import { Doc } from '@/convex/_generated/dataModel';
import { isVanillaTask, wasCompletedToday, isOverdue, isDueToday } from './task-helpers';

/**
 * List rule validation helpers based on docs/LIST_RULES.md
 */

// ============ INBOX RULES ============

/**
 * Checks if a task should appear in the Inbox list.
 * 
 * Inbox includes:
 * - Vanilla tasks (no category, priority, project, dueAt, or recurrence) that are not completed
 * - Tasks completed today (regardless of other properties)
 */
export function shouldAppearInInbox(task: Doc<'normalTasks'>): boolean {
  // Always include tasks completed today
  if (wasCompletedToday(task)) {
    return true;
  }
  
  // Include vanilla tasks that are not completed
  return isVanillaTask(task) && !task.completed;
}

// ============ TODAY RULES ============

/**
 * Checks if a task should appear in the Today list.
 * 
 * Today includes:
 * - Tasks due today
 * - Overdue tasks
 * - Vanilla tasks with this_week priority (no dueAt, no recurrence)
 * - Tasks completed today
 */
export function shouldAppearInToday(task: Doc<'normalTasks'>): boolean {
  // Tasks due today
  if (isDueToday(task)) {
    return true;
  }
  
  // Overdue tasks
  if (isOverdue(task)) {
    return true;
  }
  
  // Vanilla tasks with this_week priority
  if (isVanillaTaskWithWeeklyPriority(task)) {
    return true;
  }
  
  // Tasks completed today
  if (wasCompletedToday(task)) {
    return true;
  }
  
  return false;
}

/**
 * Checks if a task is a vanilla task with this_week priority.
 * Must not have dueAt or recurrence, but must have this_week priority.
 */
function isVanillaTaskWithWeeklyPriority(task: Doc<'normalTasks'>): boolean {
  return !task.dueAt && 
         !task.recurringTaskTemplateId && 
         task.priority === 'this_week' && 
         !task.completed;
}

// ============ PRIORITY RULES ============

/**
 * Checks if a task should appear in the Priority lists.
 * 
 * Priority lists include:
 * - Only vanilla tasks (no dueAt, no recurrence)
 * - Must have a priority set
 * - Not completed
 * - Not overdue (since overdue implies having a dueAt)
 */
export function shouldAppearInPriority(task: Doc<'normalTasks'>): boolean {
  // Must be a vanilla task in terms of scheduling
  if (task.dueAt || task.recurringTaskTemplateId) {
    return false;
  }
  
  // Must not be completed
  if (task.completed) {
    return false;
  }
  
  // Task either has a priority or goes to backlog
  return true;
}

/**
 * Checks if a task should appear in the This Week priority list.
 */
export function shouldAppearInThisWeek(task: Doc<'normalTasks'>): boolean {
  return shouldAppearInPriority(task) && task.priority === 'this_week';
}

/**
 * Checks if a task should appear in the This Month priority list.
 */
export function shouldAppearInThisMonth(task: Doc<'normalTasks'>): boolean {
  return shouldAppearInPriority(task) && task.priority === 'this_month';
}

/**
 * Checks if a task should appear in the Backlog priority list.
 */
export function shouldAppearInBacklog(task: Doc<'normalTasks'>): boolean {
  return shouldAppearInPriority(task) && !task.priority;
}